import React, {useContext, useState, useEffect} from 'react'
import {
    Button, 
    FormControl, 
    FormHelperText, 
    Grid, 
    Stack, 
    Typography, 
    Select,
    MenuItem,
    SelectChangeEvent,
    TextField,
    Alert,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Radio
} from '@mui/material'
import {AppContext} from '../../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../../StepperButtons'
import {ClinicalConditionState, ProductTypeOptions,} from '../../../contexts/types'
import {Trans} from 'react-i18next'
import {useForm} from '../../../hooks/useForm'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";
import {setClinicalCondition} from "../../../contexts/ClinicalConditionReducer";
import useTheHackiestPageNavigation from "../../../hooks/useTheHackiestPageNavigation";
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import {Theme} from '@mui/material/styles'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alertFormLabel: {
            color: 'rgb(102, 60, 0)',
            '&.Mui-focused': {
                color: 'rgb(102, 60, 0)',
            },
        },
        formControl: {
            minWidth: 300,
            marginBottom: theme.spacing(2),
        },
    })
)

// Define categories and their conditions
const CONDITION_CATEGORIES = {
    TEMPORARY: 'Temporary conditions',
    TEMPORARY_DAMAGE: 'Temporary conditions - damage of soft tissue or bone',
    PERMANENT: 'Permanent/Long-term conditions',
    NONE: 'None'
};

const ClinicalCondition: React.FC = () => {
    const classes = useStyles()
    const { navigateForward } = useTheHackiestPageNavigation()
    const { complaint, dispatch, postNotification, clearNotification, countryCode } = useContext(AppContext)
    const { t, tns, language } = useContext(OrgCustomizationContext)
    const [category, setCategory] = useState(CONDITION_CATEGORIES.NONE)
    
    const productType = complaint.generalComplaint.productType as ProductTypeOptions
    const required = { value: true, message: t('this_field_is_required') }
    
    const {
        data,
        errors,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<ClinicalConditionState>({
        initialValues: {
            ...complaint.clinicalCondition,
            // Set default clinical condition to NONE_OF_THE_ABOVE if not already set
            clinicalCondition: complaint.clinicalCondition.clinicalCondition || 'NONE_OF_THE_ABOVE'
        },
        validations: {
            clinicalCondition: { 
                required: {
                    value: (value, data) => {
                        // If "None" category is selected, validation passes
                        if (category === CONDITION_CATEGORIES.NONE) {
                            return true;
                        }
                        // For other categories, validation passes only if a valid selection is made
                        return !!(value && value !== '' && value !== 'NONE_OF_THE_ABOVE');
                    },
                    message: t('this_field_is_required'),
                }
            },
            hasPainDisappeared: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'TRANSIENT_PAIN' &&
                            data.hasPainDisappeared === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            hasNumbnessDisappeared: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'NUMBNESS' &&
                            data.hasNumbnessDisappeared === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            wereNerveSymptomsAlleviated: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition ===
                                'NERVE_INJURY_SYMPTOMS' &&
                            data.wereNerveSymptomsAlleviated === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            permanentImpairmentExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'PERMANENT_IMPAIRMENT' &&
                            !data.permanentImpairmentExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            otherExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'OTHER' &&
                            !data.otherExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            prolongedSurgeryExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'PROLONGED_SURGERY' &&
                            !data.prolongedSurgeryExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            prolongedTreatmentExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition ===
                                'PROLONGED_TREATMENT_TIME' &&
                            !data.prolongedTreatmentExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
        },
    })

    // Set the initial category based on the selected clinical condition
    useEffect(() => {
        if (data.clinicalCondition) {
            if (['ALLERGIC_REACTION', 'ASPIRATION'].includes(data.clinicalCondition)) {
                setCategory(CONDITION_CATEGORIES.TEMPORARY)
            } else if ([
                'SINUS_PERFORATION', 'BONE_FRACTURE', 'ABSCESS', 'FISTULA',
                'DAMAGING_OF_BLOOD_VESSELS', 'HAMPERS_DIAGNOSTICS', 'LOCAL_INFECTION',
                'OSTEOMYELITIS', 'REVISION_SURGERY_IMPLANT_EXTRACTION',
                'REVISION_SURGERY_ASSEMBLY_ABUTMENT_IMPLANT', 'LOCAL_INFLAMMATION',
                'TRANSIENT_PAIN', 'NUMBNESS', 'PROLONGED_SURGERY', 'PROLONGED_TREATMENT_TIME',
                'SWALLOWING_MEDICAL_DEVICE'
            ].includes(data.clinicalCondition)) {
                setCategory(CONDITION_CATEGORIES.TEMPORARY_DAMAGE)
            } else if ([
                'DEATH', 'NERVE_INJURY_SYMPTOMS', 'PERMANENT_IMPAIRMENT',
                'PERMANENT_PAIN', 'OTHER'
            ].includes(data.clinicalCondition)) {
                setCategory(CONDITION_CATEGORIES.PERMANENT)
            } else if (data.clinicalCondition === 'NONE_OF_THE_ABOVE') {
                setCategory(CONDITION_CATEGORIES.NONE)
            } else {
                setCategory(CONDITION_CATEGORIES.NONE)
            }
        }
    }, [data.clinicalCondition])

    // Set initial clinical condition to 'NONE_OF_THE_ABOVE' when component loads with None category
    useEffect(() => {
        if (category === CONDITION_CATEGORIES.NONE && !data.clinicalCondition) {
            handleChange('clinicalCondition', 'NONE_OF_THE_ABOVE')
        }
    }, [category, data.clinicalCondition, handleChange])

    const navigateToNextScreen = () => navigateForward()

    const handleCategoryChange = (event: SelectChangeEvent) => {
        const selectedCategory = event.target.value
        setCategory(selectedCategory)
        
        // If "None" is selected, set clinical condition to 'NONE_OF_THE_ABOVE'
        // Otherwise, reset clinical condition when changing category
        if (selectedCategory === CONDITION_CATEGORIES.NONE) {
            handleChange('clinicalCondition', 'NONE_OF_THE_ABOVE')
        } else {
            handleChange('clinicalCondition', '')
            // Force validation refresh for non-None categories
            setTimeout(() => {
                enableOnChangeValidation(true)
            }, 0)
        }
    }

    const handleConditionChange = (event: SelectChangeEvent) => {
        handleChange('clinicalCondition', event.target.value)
    }

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Always validate the form fields - the validation function handles "None" vs other categories
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing
        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const clinicalCondition: ClinicalConditionState = {
            ...data,
            permanentImpairmentExplanation:
                data.clinicalCondition === 'PERMANENT_IMPAIRMENT'
                    ? data.permanentImpairmentExplanation
                    : '',
            otherExplanation:
                data.clinicalCondition === 'OTHER' ? data.otherExplanation : '',
            prolongedSurgeryExplanation:
                data.clinicalCondition === 'PROLONGED_SURGERY'
                    ? data.prolongedSurgeryExplanation
                    : '',
            prolongedTreatmentExplanation:
                data.clinicalCondition === 'PROLONGED_TREATMENT_TIME'
                    ? data.prolongedTreatmentExplanation
                    : '',
            hasPainDisappeared:
                data.clinicalCondition === 'TRANSIENT_PAIN'
                    ? data.hasPainDisappeared
                    : '',
            hasNumbnessDisappeared:
                data.clinicalCondition === 'NUMBNESS'
                    ? data.hasNumbnessDisappeared
                    : '',
            wereNerveSymptomsAlleviated:
                data.clinicalCondition === 'NERVE_INJURY_SYMPTOMS'
                    ? data.wereNerveSymptomsAlleviated
                    : '',
        }
        
        dispatch(setClinicalCondition(clinicalCondition))
        navigateToNextScreen()
    }

    // Helper function to determine if a condition should be shown for current product type
    const shouldShowForProductType = (condition: string) => {
        const implantOnly = ['FISTULA', 'OSTEOMYELITIS', 'REVISION_SURGERY_IMPLANT_EXTRACTION']
        const implantAbutment = ['HAMPERS_DIAGNOSTICS', 'REVISION_SURGERY_ASSEMBLY_ABUTMENT_IMPLANT']
        const implantInstrument = ['SINUS_PERFORATION', 'BONE_FRACTURE', 'ABSCESS', 'DAMAGING_OF_BLOOD_VESSELS']
        const implantAbutmentInstrument = ['TRANSIENT_PAIN', 'NUMBNESS', 'PERMANENT_PAIN']
        const implantAbutmentScrewInstrument = ['PROLONGED_SURGERY', 'SWALLOWING_MEDICAL_DEVICE', 'DEATH', 'PERMANENT_IMPAIRMENT']
        
        if (implantOnly.includes(condition) && productType !== 'IMPLANT') {
            return false
        }
        
        if (implantAbutment.includes(condition) && 
            !['IMPLANT', 'ABUTMENT', 'SCREW'].includes(productType)) {
            return false
        }
        
        if (implantInstrument.includes(condition) && 
            !['IMPLANT', 'CLINICAL_INSTRUMENT'].includes(productType)) {
            return false
        }
        
        if (implantAbutmentInstrument.includes(condition) && 
            !['IMPLANT', 'ABUTMENT', 'CLINICAL_INSTRUMENT'].includes(productType)) {
            return false
        }
        
        if (condition === 'NERVE_INJURY_SYMPTOMS' && 
            !['IMPLANT', 'CLINICAL_INSTRUMENT'].includes(productType)) {
            return false
        }
        
        return true
    }

    // Render additional fields based on the selected clinical condition
    const renderAdditionalFields = () => {
        switch (data.clinicalCondition) {
            case 'TRANSIENT_PAIN':
                return (
                    <Alert severity='warning' icon={false}>
                        <FormControl>
                            <FormLabel className={classes.alertFormLabel}>
                                {t('cci_has_pain_disappeared')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.hasPainDisappeared}
                                onChange={(e) => handleChange('hasPainDisappeared', e.target.value)}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio />}
                                    label='Yes'
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio />}
                                    label='No'
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.hasPainDisappeared}>
                                {errors.hasPainDisappeared}
                            </FormHelperText>
                        </FormControl>
                    </Alert>
                )
            case 'NUMBNESS':
                return (
                    <Alert severity='warning' icon={false}>
                        <FormControl>
                            <FormLabel className={classes.alertFormLabel}>
                                {t('cci_has_numbness_disappeared')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.hasNumbnessDisappeared}
                                onChange={(e) => handleChange('hasNumbnessDisappeared', e.target.value)}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio />}
                                    label='Yes'
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio />}
                                    label='No'
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.hasNumbnessDisappeared}>
                                {errors.hasNumbnessDisappeared}
                            </FormHelperText>
                        </FormControl>
                    </Alert>
                )
            case 'NERVE_INJURY_SYMPTOMS':
                return (
                    <Alert severity='warning' icon={false}>
                        <FormControl>
                            <FormLabel className={classes.alertFormLabel}>
                                {t('cci_were_nerve_symptoms_alleviated')}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={data.wereNerveSymptomsAlleviated}
                                onChange={(e) => handleChange('wereNerveSymptomsAlleviated', e.target.value)}
                            >
                                <FormControlLabel
                                    value='YES'
                                    control={<Radio />}
                                    label='Yes'
                                />
                                <FormControlLabel
                                    value='NO'
                                    control={<Radio />}
                                    label='No'
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.wereNerveSymptomsAlleviated}>
                                {errors.wereNerveSymptomsAlleviated}
                            </FormHelperText>
                        </FormControl>
                    </Alert>
                )
            case 'PERMANENT_IMPAIRMENT':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='permanentImpairmentExplanation'
                        error={!!errors.permanentImpairmentExplanation}
                        helperText={errors.permanentImpairmentExplanation}
                        value={data.permanentImpairmentExplanation}
                        onChange={(e) => handleChange('permanentImpairmentExplanation', e.target.value)}
                    />
                )
            case 'OTHER':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='otherExplanation'
                        error={!!errors.otherExplanation}
                        helperText={errors.otherExplanation}
                        value={data.otherExplanation}
                        onChange={(e) => handleChange('otherExplanation', e.target.value)}
                    />
                )
            case 'PROLONGED_SURGERY':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='prolongedSurgeryExplanation'
                        error={!!errors.prolongedSurgeryExplanation}
                        helperText={errors.prolongedSurgeryExplanation}
                        value={data.prolongedSurgeryExplanation}
                        onChange={(e) => handleChange('prolongedSurgeryExplanation', e.target.value)}
                    />
                )
            case 'PROLONGED_TREATMENT_TIME':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='prolongedTreatmentExplanation'
                        error={!!errors.prolongedTreatmentExplanation}
                        helperText={errors.prolongedTreatmentExplanation}
                        value={data.prolongedTreatmentExplanation}
                        onChange={(e) => handleChange('prolongedTreatmentExplanation', e.target.value)}
                    />
                )
            default:
                return null
        }
    }

    // Get options for condition dropdown based on selected category
    const getConditionOptions = () => {
        if (category === CONDITION_CATEGORIES.NONE) {
            return []
        }
        
        if (category === CONDITION_CATEGORIES.TEMPORARY) {
            return [
                { value: 'ALLERGIC_REACTION', label: t('cci_option_allergic_reaction') },
                { value: 'ASPIRATION', label: t('cci_option_aspiration') }
            ].filter(option => shouldShowForProductType(option.value))
        }
        
        if (category === CONDITION_CATEGORIES.TEMPORARY_DAMAGE) {
            return [
                { value: 'SINUS_PERFORATION', label: t('cci_option_sinus_perforation') },
                { value: 'BONE_FRACTURE', label: t('cci_option_bone_fracture') },
                { value: 'ABSCESS', label: t('cci_option_abscess') },
                { value: 'FISTULA', label: t('cci_option_fistula') },
                { value: 'DAMAGING_OF_BLOOD_VESSELS', label: t('cci_option_damaging_of_blood_vessels') },
                { value: 'HAMPERS_DIAGNOSTICS', label: t('cci_option_hampers_diagnostics') },
                { value: 'LOCAL_INFECTION', label: t('cci_option_local_infection') },
                { value: 'OSTEOMYELITIS', label: t('cci_option_osteomyelitis') },
                { value: 'REVISION_SURGERY_IMPLANT_EXTRACTION', label: t('cci_option_revision_surgery_implant_extraction') },
                { value: 'REVISION_SURGERY_ASSEMBLY_ABUTMENT_IMPLANT', label: t('cci_option_revision_surgery_assembly_abutment_implant') },
                { value: 'LOCAL_INFLAMMATION', label: t('cci_option_local_inflammation') },
                { value: 'TRANSIENT_PAIN', label: t('cci_option_transient_pain') },
                { value: 'NUMBNESS', label: t('cci_option_numbness') },
                { value: 'PROLONGED_SURGERY', label: t('cci_option_prolonged_surgery') },
                { value: 'PROLONGED_TREATMENT_TIME', label: t('cci_option_prolonged_treatment_time') },
                { value: 'SWALLOWING_MEDICAL_DEVICE', label: t('cci_option_swallowing_medical_device') }
            ].filter(option => shouldShowForProductType(option.value))
        }
        
        if (category === CONDITION_CATEGORIES.PERMANENT) {
            return [
                { value: 'NERVE_INJURY_SYMPTOMS', label: t('cci_option_nerve_injury_symptoms') },
                { value: 'PERMANENT_PAIN', label: t('cci_option_permanent_pain') },
                { value: 'OTHER', label: t('cci_option_other') }
            ].filter(option => shouldShowForProductType(option.value))
        }
        
        return []
    }

    return (
        <Grid container>
            <Stack spacing={2} sx={{width: '100%'}}>
                <Typography variant='h5' sx={{ mb: 4 }}>
                    {t('cci_clinical_conditions')} -{' '}
                    {t(`pt_option_${productType.toLowerCase()}`).toLowerCase()}
                </Typography>

               

                {/* Category dropdown - 'None' is included as an option */}
                <FormControl  sx={{ mb: 2 }}>
                    <Typography variant='h6' gutterBottom>
                        {t('cci_select_clinical_condition_category')}
                    </Typography>
                    <Select
                        value={category}
                        onChange={handleCategoryChange}
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value={CONDITION_CATEGORIES.TEMPORARY}>
                            {t(countryCode === 'jp' && language === 'ja' ? 'cci_temporary_conditions_jp' : 'cci_temporary_conditions')}
                        </MenuItem>
                        <MenuItem value={CONDITION_CATEGORIES.TEMPORARY_DAMAGE}>
                            {t('cci_temporary_conditions_damage')}
                        </MenuItem>
                        <MenuItem value={CONDITION_CATEGORIES.PERMANENT}>
                            {t(countryCode === 'jp' && language === 'ja' ? 'cci_permanent_long_term_conditions_jp' : 'cci_permanent_long_term_conditions')}
                        </MenuItem>
                        <MenuItem value={CONDITION_CATEGORIES.NONE}>
                            {t('cci_option_none_of_the_above')}
                        </MenuItem>
                    </Select>
                </FormControl>

                {/* Condition dropdown only if not 'None' */}
                {category !== CONDITION_CATEGORIES.NONE && (
                    <FormControl  sx={{ mb: 2 }}>
                        <Typography variant='h6' gutterBottom>
                            {category === CONDITION_CATEGORIES.TEMPORARY && t(countryCode === 'jp' && language === 'ja' ? 'cci_temporary_conditions_jp' : 'cci_temporary_conditions')}
                            {category === CONDITION_CATEGORIES.TEMPORARY_DAMAGE && t('cci_temporary_conditions_damage')}
                            {category === CONDITION_CATEGORIES.PERMANENT && t(countryCode === 'jp' && language === 'ja' ? 'cci_permanent_long_term_conditions_jp' : 'cci_permanent_long_term_conditions')}
                        </Typography>
                        <Select
                            value={data.clinicalCondition}
                            onChange={handleConditionChange}
                            error={!!errors.clinicalCondition}
                            displayEmpty                          
                        >
                            {/* <MenuItem value="">
                                <em>Select a condition</em>
                            </MenuItem> */}
                            {getConditionOptions().map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText error={!!errors.clinicalCondition}>
                            {errors.clinicalCondition}
                        </FormHelperText>
                    </FormControl>
                )}

                {/* Additional fields based on selected condition */}
                {renderAdditionalFields()}
            </Stack>

            <Stack direction='row-reverse' spacing={2} sx={{ pt: 4, width: '100%' }}>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>
                
                <PreviousButton before={() => dispatch(setClinicalCondition(data))} />
                
                <CancelButton />
            </Stack>
        </Grid>
    )
}

export default ClinicalCondition
